/**
 * Hook wrapper around the Review-queue drawer reactive variable (SE-5499).
 *
 * Exposes the current drawer state plus imperative open/close helpers so any
 * surface can drive the queue without touching the raw `makeVar`. Mirrors
 * `features/contracts/hooks/useContractDrawerState`.
 * @module features/player-kanban/hooks/useReviewQueueDrawerState
 */

import { useReactiveVar } from "@apollo/client";
import { useCallback, useMemo } from "react";

import {
  type ReviewQueueDrawerState,
  resetReviewQueueDrawer,
  reviewQueueDrawerVar,
} from "../stores/reviewQueueDrawerStore";

/** Public API returned by {@link useReviewQueueDrawerState}. */
export interface UseReviewQueueDrawerStateResult {
  /** Current drawer state. */
  readonly state: ReviewQueueDrawerState;
  /**
   * Open the drawer, optionally scoped to one board.
   * @param kanbanBoardId - Board scope; omit for the whole-org queue.
   */
  readonly open: (kanbanBoardId?: string | null) => void;
  /** Close the drawer. */
  readonly close: () => void;
}

/**
 * Subscribe to the Review-queue drawer reactive variable + open/close helpers.
 * @returns The {@link UseReviewQueueDrawerStateResult}.
 */
export function useReviewQueueDrawerState(): UseReviewQueueDrawerStateResult {
  const state = useReactiveVar(reviewQueueDrawerVar);

  const open = useCallback(
    (kanbanBoardId: string | null = null) =>
      reviewQueueDrawerVar({ open: true, kanbanBoardId }),
    []
  );

  const close = useCallback(() => resetReviewQueueDrawer(), []);

  return useMemo(() => ({ state, open, close }), [state, open, close]);
}
